import { ArrowUp, Network } from 'lucide-react'
import { formatCurrency } from '@/utils/format-currency'
import type { Partner, PartnerNetworkNode } from '@/types'

interface PartnerDetailNetworkTabProps {
  partner: Partner
  onSelectPartner?: (partnerId: string) => void
}

function NetworkRows({
  nodes,
  level,
  onSelectPartner,
}: {
  nodes: PartnerNetworkNode[]
  level: number
  onSelectPartner?: (partnerId: string) => void
}) {
  return (
    <>
      {nodes.map((node) => (
        <div key={node.id}>
          <button
            type='button'
            onClick={() => onSelectPartner?.(node.id)}
            className='flex w-full items-center justify-between px-4 py-2.5 text-left transition-colors hover:bg-[var(--glass-bg)]'
            style={{ paddingLeft: `${16 + (level - 1) * 20}px` }}
          >
            <span className='flex items-center gap-2 text-sm text-[var(--font-color-body)]'>
              <span className='rounded bg-[var(--glass-bg)] px-1.5 py-0.5 text-[10px] font-bold text-[var(--text-muted)]'>
                {level === 1 ? 'Direto' : `${level}º grau`}
              </span>
              <span className={node.indicacaoAtiva ? '' : 'opacity-60'}>{node.nome}</span>
            </span>
            <span className='text-xs font-semibold text-[var(--brand-primary)]'>{Number(node.comissao)}%</span>
          </button>
          {node.indicados && node.indicados.length > 0 && (
            <NetworkRows nodes={node.indicados} level={level + 1} onSelectPartner={onSelectPartner} />
          )}
        </div>
      ))}
    </>
  )
}

function countNetwork(nodes: PartnerNetworkNode[] = []): number {
  return nodes.reduce((sum, n) => sum + 1 + countNetwork(n.indicados), 0)
}

export function PartnerDetailNetworkTab({ partner, onSelectPartner }: PartnerDetailNetworkTabProps) {
  const indicados = partner.indicados ?? []

  return (
    <div className='space-y-4'>
      <div className='grid grid-cols-3 gap-3'>
        <div className='glass-card'>
          <p className='text-[11px] uppercase tracking-wider text-[var(--font-color-label)]'>Indicado por</p>
          {partner.indicadoPor ? (
            <button
              type='button'
              onClick={() => onSelectPartner?.(partner.indicadoPor!.id)}
              className='mt-1 flex items-center gap-1.5 text-sm font-semibold text-[var(--brand-primary)] hover:underline'
            >
              <ArrowUp size={13} />
              {partner.indicadoPor.nome}
            </button>
          ) : (
            <p className='mt-1 text-sm font-semibold text-[var(--font-color-body)]'>Topo da pirâmide</p>
          )}
        </div>
        <div className='glass-card'>
          <p className='text-[11px] uppercase tracking-wider text-[var(--font-color-label)]'>Ganho como venda direta</p>
          <p className='mt-1 text-sm font-bold text-emerald-500'>{formatCurrency(partner.stats?.directEarnings ?? 0)}</p>
        </div>
        <div className='glass-card'>
          <p className='text-[11px] uppercase tracking-wider text-[var(--font-color-label)]'>Ganho como rede</p>
          <p className='mt-1 text-sm font-bold text-emerald-500'>{formatCurrency(partner.stats?.networkEarnings ?? 0)}</p>
        </div>
      </div>

      <div className='glass-card p-0 overflow-hidden'>
        <div className='flex items-center justify-between border-b border-[var(--glass-border)] px-4 py-3'>
          <p className='flex items-center gap-1.5 text-[var(--font-color-label)] uppercase tracking-wider font-medium' style={{ fontSize: 'var(--font-size-label)' }}>
            <Network size={12} />
            Indicados
          </p>
          <span className='text-xs text-[var(--font-color-muted)]'>{countNetwork(indicados)} na rede</span>
        </div>
        <div className='divide-y divide-[var(--glass-border)]'>
          {indicados.length > 0 ? (
            <NetworkRows nodes={indicados} level={1} onSelectPartner={onSelectPartner} />
          ) : (
            <div className='px-4 py-6 text-center text-sm text-[var(--font-color-muted)]'>Nenhum parceiro indicado</div>
          )}
        </div>
      </div>
    </div>
  )
}
